<template lang="pug">
.schedules-filters.d-flex.flex-column
  .d-flex.flex-column.flex-lg-row.gap-15.flex-md-nowrap.mb-8
    .d-flex.align-items-end.justify-content-between.gap-15.mb-md-0.mb-16.w-full.flex-wrap
      h2.page-heading.mb-0 Schedules

      .actions
        SchedulesFiltersActionsDropdown(@open-dialog="openDialog")

  .d-flex.align-items-start.justify-content-between.gap-15.flex-xl-nowrap.w-full.flex-wrap
    .dates-filter.d-flex.flex-column.align-items-start
      label.control-label.invisible Dates
      .date-range.d-flex.flex-sm-row.flex-column.mb-8.gap-10
        SchedulesFiltersArrowButtons(@change="changeDateRange")
        AppDatePicker.w-full(
          :type="datePickerType"
          :start-date="toMoment(startDate)"
          :end-date="toMoment(stopDate)"
          :value="toMoment(stopDate)"
          :selected-date="toMoment(today)"
          :start-week-on="startWeekOn"
          :min-date="minDate"
          :max-date="maxDate"
          :show-quick-pick="false"
          opens="center"
          @input="onDatePickerChange"
        )
        .d-flex.gap-5
          AppButton.qa-today(@click="onTodayClick")
            | Today
          .timezone-dropdown
            AppTimeZoneDropdown(
              :active="activeTimeZone"
              :options="timeZoneOptions"
              direction="right"
              @input="onTimeZoneChange"
            )

    .events-teams-members-filters.d-flex.flex-column.justify-content-end.gap-10
      .teams-members-filters.d-flex.flex-sm-nowrap.w-full.flex-wrap.gap-10
        .teams-filter
          AppLazySelect(
            eager
            multiple
            as-filter
            fit-parent
            append-to-body
            label="Teams"
            title="Select teams"
            resource="team"
            :fetcher="teamsFetcher"
            :value="changedTeams"
            @input="onTeamsSelect"
          )
            template(#item-leading="{ item }")
              AppAvatarInitial(
                :text="item.name"
                size="speck"
              )
        .members-filter.position-relative
          AppLazySelect(
            ref="members-selector"
            eager
            multiple
            as-filter
            fit-parent
            append-to-body
            label="Members"
            :value="changedUsers"
            title="Select members"
            resource="member"
            :fetcher="membersFetcher"
            @input="onMembersSelect"
          )
            template(#item-leading="{ item }")
              AppAvatar(
                v-if="item.avatar"
                :avatar="item.avatar"
                size="speck"
              )

      .events-filter.d-flex.align-items-center.justify-content-end.flex-sm-row.flex-column.gap-10
        label.control-label.mb-0 Events:
        .d-flex.gap-20
          AppCheckbox(
            v-for="eventType in enabledEventTypes"
            :key="eventType.id"
            :value="eventsFilter?.includes(eventType.id)"
            @input="onEventTypeCheck(eventType.id, $event)"
          ) {{ eventType.text }}
</template>

<script>
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { mapInertiaProps } from '~/utils/inertia';
import { Formats, parseInternalDate, formatInternalDate } from '~/utils/time_utils';
import SchedulesFiltersArrowButtons from '~/components/attendance_schedules/SchedulesFiltersArrowButtons.vue';
import SchedulesFiltersActionsDropdown from '~/components/attendance_schedules/SchedulesFiltersActionsDropdown.vue';
import { AttendanceSchedulesResource } from '~/resources';

export default {
  name: 'SchedulesFilters',
  components: { SchedulesFiltersArrowButtons, SchedulesFiltersActionsDropdown },
  data: () => ({
    changedTeams: {
      items: [],
      selectAll: false,
    },
    changedUsers: {
      items: [],
      selectAll: false,
    },
  }),
  computed: {
    ...mapInertiaProps({
      timeZoneOptions: 'time_zones',
      minDate: 'min_date',
      maxDate: 'max_date',
      activeView: 'active_view',
      eventsFilter: 'events_filter',
      enabledEventTypes: (props) => props.enabled_event_types.map(([text, id]) => ({ id, text })),
      startWeekOn: ({ organization }) => organization.start_week_on,
      startDate: ({ start_date: startDate }) => dayjs(startDate),
      stopDate: ({ stop_date: stopDate }) => dayjs(stopDate),
      activeTimeZone: 'time_zone',
      userIds: 'filtered_user_ids',
      teamIds: 'filtered_team_ids',
      selectAll: 'select_all',
      teamsEnabled: 'teams_enabled',
      members({ members }) {
        return members
          .map(({ id, name: text, avatar }) => ({ id, text, avatar }))
          .sort((ua, ub) => ua.text.localeCompare(ub.text));
      },
      organizationId: ({ organization }) => organization.id,
      currentUserRole: 'current_user_role',
    }),
    today() {
      return dayjs();
    },
    isoWeekday() {
      return toMomentIsoWeekday(this.startWeekOn);
    },
    datePickerType() {
      const map = { month: 'monthly', week: 'weekly', day: 'weekly' };

      return map[this.activeView];
    },
  },
  watch: {
    teamIds: {
      immediate: true,
      handler(teamIds) {
        this.changedTeams = {
          items: teamIds,
          selectAll: this.selectAll?.includes('teams') ?? false,
        };
      },
    },
    userIds: {
      immediate: true,
      handler(userIds) {
        this.changedUsers = {
          items: userIds,
          selectAll: this.selectAll?.includes('users') ?? false,
        };
      },
    },
  },
  mounted() {
    dayjs.extend(isoWeek);
    this.$refs['members-selector'].fetchItems();
  },
  methods: {
    membersFetcher(payload) {
      const finalPayload = {
        ...payload,
        filters: {
          ...payload.filters,
          // Send the `team_ids` to the backend to be able to select the members automatically
          team_ids: this.changedTeams.items,
          select_all_teams: this.changedTeams.selectAll,
        },
      };

      return AttendanceSchedulesResource.members({ organization_id: this.organizationId }, finalPayload, {
        abortReload: true,
      });
    },
    teamsFetcher(payload) {
      return AttendanceSchedulesResource.teams({ organization_id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    onTeamsSelect(changedTeams) {
      // 1. Select the TEAMS
      this.changedTeams = changedTeams;
      // 2. Trigger a `this.$refs['members-selector'].onSelectAll()`
      this.$refs['members-selector'].onSelectAll();
      // 3. Trigger the URL change with the `team_ids` & `user_ids`.
      //    This will be automatically triggered after the `members-selector`
      //    comes back with the result of `SELECT_ALL` automatically.
    },
    onMembersSelect({ selectAll, items }) {
      const filters = [
        { key: 'filtered_user_ids', value: items?.length ? items.join('-') : '' },
        {
          key: 'filtered_team_ids',
          value: this.changedTeams?.items?.length ? this.changedTeams?.items?.join('-') : '',
        },
        {
          key: 'select_all',
          value: [selectAll && 'users', this.changedTeams.selectAll && 'teams'].filter(Boolean).join('-'),
        },
      ];

      this.filterChange(filters, { preserveState: true });
    },

    onTimeZoneChange(tzName, tzDescription) {
      this.filterChange(
        [
          { key: 'time_zone', value: tzName },
          { key: 'time_zone_type', value: tzDescription },
        ],
        { preserveScroll: true },
      );
    },
    onTodayClick() {
      if (this.activeView === 'month') {
        const today = dayjs();

        return this.changeDateRange(today.startOf('month'), today.endOf('month'));
      }

      const today = moment.utc();
      const startDay = formatInternalDate(today.startOfIsoWeek(this.isoWeekday));
      const stopDay = formatInternalDate(today.endOfIsoWeek(this.isoWeekday));

      return this.changeDateRange(moment(startDay), moment(stopDay));
    },
    onDatePickerChange(date) {
      const newDate = parseInternalDate(date);

      if (this.activeView === 'month') {
        // clone() is needed. Otherwise, moment.js will mutate newDate
        // and both changeDateRange arguments will be equal to the end of the month.
        return this.changeDateRange(newDate.clone().startOf('month'), newDate.clone().endOf('month'));
      }

      const startDate = formatInternalDate(newDate.startOfIsoWeek(this.isoWeekday));
      const endDate = formatInternalDate(newDate.endOfIsoWeek(this.isoWeekday));

      return this.changeDateRange(moment(startDate), moment(endDate));
    },
    changeDateRange(startDate, stopDate) {
      window.analytics?.track('Calendar table date selected', {
        organization_id: this.organizationId,
        user_role: this.currentUserRole,
        current_page_title: document.title,
        current_url: document.url,
        preset_used: `${this.startDate.format('YYYY-MM-DD')} - ${this.stopDate.format('YYYY-MM-DD')}`,
        preset_value: `${startDate.format('YYYY-MM-DD')} - ${stopDate.format('YYYY-MM-DD')}`,
      });
      this.filterChange(
        [
          { key: 'start_date', value: this.iso8601(startDate) },
          { key: 'stop_date', value: this.iso8601(stopDate) },
        ],
        { preserveScroll: true },
      );
    },
    onEventTypeCheck(eventType, checked) {
      const newEventTypes = checked
        ? [...this.eventsFilter, eventType]
        : this.eventsFilter.filter((type) => type !== eventType);

      this.filterChange([{ key: 'events_filter', value: newEventTypes }], { preserveState: true });
    },
    filterChange(newFilterValue, options = {}) {
      this.$emit('filter-change', newFilterValue, options);
    },
    toMoment(date) {
      return moment.utc(this.iso8601(date), Formats.date.iso);
    },
    iso8601(date) {
      return date.format(Formats.date.iso);
    },
    createdNestedOptions(title, options) {
      return [
        {
          text: title,
          children: options,
        },
      ];
    },
    openDialog(dialog) {
      this.$emit('open-dialog', dialog);
    },
  },
};
</script>
